import { useMediaQuery } from '@uidotdev/usehooks';
import parse from 'html-react-parser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useBem from '@indicia/use-bem';

import Dialog from '../../../../shared/components/dialog/Dialog';
import RenderMode from '../../../../shared/models/survey/RenderMode';
import SurveyAnswer from '../../../../shared/models/survey/SurveyAnswer';
import SurveyQuestion from '../../../../shared/models/survey/SurveyQuestion';
import SurveyQuestionChapterIntroduction from '../../../../shared/models/survey/SurveyQuestionChapterIntroduction';
import SurveyQuestionMultipleChoice from '../../../../shared/models/survey/SurveyQuestionMultipleChoice';
import SurveyQuestionNumericSlider from '../../../../shared/models/survey/SurveyQuestionNumericSlider';
import SurveyQuestionType from '../../../../shared/models/survey/SurveyQuestionType';
import useSurveyContext from '../../store/UseSurveyContext';
import QuestionTitle from '../questionTitle/QuestionTitle';
import SurveyChapterIntroductionQuestion from '../surveyChapterIntroduction/SurveyChapterIntroductionQuestion';
import SurveyDropdownQuestion from '../surveyDropdown/SurveyDropdownQuestion';
import SurveyInputQuestion from '../surveyInput/SurveyInputQuestion';
import SurveyIntroductionQuestion from '../surveyIntroduction/SurveyIntroductionQuestion';
import SurveyMultipleChoiceQuestion from '../surveyMultipleChoice/SurveyMultipleChoiceQuestion';
import SurveyRadioQuestion from '../surveyRadio/survey-radio-question';

type SurveyQuestionPropType = {
  question: SurveyQuestion;
};

const SurveyQuestionRenderer = ({ question }: SurveyQuestionPropType) => {
  const { bemClassName } = useBem('survey-question');
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const isSmallDevice = useMediaQuery('only screen and (max-width: 998px)');
  const { questionAnswers, setQuestionAnswer, getMetadata } = useSurveyContext();
  const { questionType, description, questionId, title, imageIdentifier, info, suffix } = question;

  const handleAnswer = (answer: SurveyAnswer) => {
    setQuestionAnswer(answer);
  };

  const imageUrl = `${window.__RUNTIME_CONFIG__.ODATA_URL}/${getMetadata(imageIdentifier)}`;

  const objIndex = questionAnswers.findIndex((obj) => obj.questionId === questionId);
  const previousAnswer = objIndex !== -1 ? questionAnswers[objIndex] : null;

  const renderQuestionType = () => {
    if (questionType === SurveyQuestionType.NUMERIC_SLIDER || questionType === SurveyQuestionType.NUMERIC_INPUT) {
      const { max, min, identifier } = question as SurveyQuestionNumericSlider;

      return (
        <SurveyInputQuestion
          question={title}
          key={questionId}
          id={questionId}
          minValue={min}
          maxValue={max}
          suffix={suffix}
          onSelectAnswer={(answer: SurveyAnswer) => handleAnswer(answer)}
          previousAnswer={(previousAnswer?.value as number) ?? undefined}
          bem={bemClassName}
          identifier={identifier}
        />
      );
    }

    const { options, renderMode } = question as SurveyQuestionMultipleChoice;

    if (questionType === SurveyQuestionType.INTRODUCTION) {
      return (
        <SurveyIntroductionQuestion
          key={questionId}
          id={questionId}
          onAnswerSelect={(answer: SurveyAnswer) => handleAnswer(answer)}
          previousAnswer={(previousAnswer?.value as string[]) ?? undefined}
          question={title}
          options={options}
          bem={bemClassName}
        />
      );
    }

    if (questionType === SurveyQuestionType.CHAPTER_INTRODUCTION) {
      const { chapterList, showInChapterList } = question as SurveyQuestionChapterIntroduction;

      if (!showInChapterList) return null;

      return (
        <SurveyChapterIntroductionQuestion
          key={questionId}
          title={title}
          chapters={chapterList}
        />
      );
    }

    if (questionType === SurveyQuestionType.MULTIPLE_CHOICE && renderMode === RenderMode.Dropdown) {
      return (
        <SurveyDropdownQuestion
          key={questionId}
          id={questionId}
          onAnswerSelect={(answer: SurveyAnswer) => handleAnswer(answer)}
          previousAnswer={(previousAnswer?.value as string[]) ?? undefined}
          question={title}
          options={options}
          bem={bemClassName}
        />
      );
    }

    if (questionType === SurveyQuestionType.MULTIPLE_CHOICE && options.length > 2) {
      return (
        <SurveyRadioQuestion
          key={questionId}
          id={questionId}
          onAnswerSelect={(answer: SurveyAnswer) => handleAnswer(answer)}
          previousAnswer={(previousAnswer?.value as string[]) ?? undefined}
          question={title}
          options={options}
          bem={bemClassName}
        />
      );
    }

    return (
      <SurveyMultipleChoiceQuestion
        key={questionId}
        id={questionId}
        onAnswerSelect={(answer: SurveyAnswer) => handleAnswer(answer)}
        previousAnswer={(previousAnswer?.value as string[]) ?? undefined}
        question={title}
        options={options}
        bem={bemClassName}
        renderMode={renderMode}
      />
    );
  };

  const isIntroduction =
    questionType === SurveyQuestionType.INTRODUCTION || questionType === SurveyQuestionType.CHAPTER_INTRODUCTION;

  return (
    <div className="survey-question-container">
      <div className={bemClassName()}>
        <div className={bemClassName('content')}>
          <div className={bemClassName('title')}>
            <QuestionTitle
              title={title}
              questionId={questionId}
            />
          </div>
          <div className={bemClassName('description')}>{parse(description ?? '')}</div>
          {info && (
            <button
              type="button"
              className={bemClassName('info-button')}
              onClick={() => setShowDialog(true)}
              title={t('survey.info')!}
            >
              <span>i</span>
              {t('survey.info')}
            </button>
          )}
          {renderQuestionType()}
        </div>
        {!isSmallDevice && isIntroduction && (
          <div className={bemClassName('image')}>
            <img
              alt=""
              src={imageUrl}
            />
          </div>
        )}
      </div>
      {showDialog && (
        <Dialog
          content={<p>{info}</p>}
          onClose={() => setShowDialog(false)}
        />
      )}
    </div>
  );
};

export default SurveyQuestionRenderer;
